import * as Sentry from '@sentry/react';
import { Browser } from '@capacitor/browser';
import { App as CapacitorApp } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { platformService } from './platform';
import { coreApiClient } from './api/core';
import { PaywallPlugin } from './paywallPlugin';
import type { PaywallCapabilities, PaywallShowOptions } from './paywallPlugin';
import { useAppStore } from '@/store/appStore';

/** Server view of a Stripe checkout session (GET /api/payments/checkout-status). */
interface CheckoutStatus {
    paid: boolean;
    status: 'open' | 'complete' | 'expired' | string;
    is_pro: boolean;
    /** Gift checkouts only: how many of its gifts exist yet. */
    gift_codes?: number;
}

/**
 * App Store product id of the GreekBrain AI monthly subscription. Must match
 * AppConfig.greekbrain.iapProductId (iOS) and billing.apple.greekbrain.product_id
 * (backend) — change all three together.
 */
export const GREEKBRAIN_IOS_PRODUCT_ID = 'app.greekbrain.ai.monthly';

// In-app checkout pacing. The user is typing card details in a browser sheet, so
// the ceiling is generous; the grace window covers the gap between Stripe
// redirecting and entitlement being confirmed.

const CHECKOUT_POLL_MS = 2500;
const CHECKOUT_TICK_MS = 500;
const CHECKOUT_TIMEOUT_MS = 15 * 60 * 1000;
const CHECKOUT_GRACE_MS = 20_000;
// After returning, how long to keep polling when the server hasn't answered once.
const CHECKOUT_UNREACHABLE_MS = 60_000;
const CHECKOUT_POLL_TIMEOUT_MS = 8000;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface PaymentResult {
    success: boolean;
    checkoutUrl?: string;
    cancelled?: boolean;
    error?: string;
}

/** Options for the one-time Hallouminati purchase (tiered + refer-a-friend). */
export type PurchaseOptions = PaywallShowOptions;

export interface ReferralCodeValidation {
    valid: boolean;
    reason?: string;
    code?: string;
    discount_percent?: number;
    expires_at?: string;
}

export type PaymentMethod = 'native' | 'stripe';

class PaymentService {
    getPaymentMethod(): PaymentMethod {
        // Native in-app billing on both iOS (StoreKit) and Android (Google Play);
        // web/Telegram fall back to Stripe Checkout.
        if (!platformService.isNative()) {
            return 'stripe';
        }
        // …except in regions the server flags, where the store rails are
        // unreliable and the app buys through Stripe Checkout in an in-app
        // browser instead (see completeNativeStripeCheckout).
        return this.isStripePaywallRegion() ? 'stripe' : 'native';
    }

    /**
     * Server-decided per-account flag (UserDTO features.stripe_paywall). Read
     * from the store on every call rather than cached — it lands with the
     * profile, which can arrive after the paywall first renders.
     */
    isStripePaywallRegion(): boolean {
        return useAppStore.getState().user?.features?.stripe_paywall === true;
    }

    async purchase(options?: PurchaseOptions): Promise<PaymentResult> {
        if (this.getPaymentMethod() === 'native') {
            return this.purchaseViaNative(options);
        }
        return this.purchaseViaStripe(options);
    }

    /**
     * Buy `quantity` Hallouminati gifts (docs/hallouminati/gift-codes.md).
     * Stripe only, wherever the Adept purchase is Stripe too: web, Telegram,
     * and the apps in Stripe-paywall regions. Web gets the checkout URL back to
     * redirect to; the apps run the checkout in-app and resolve when the
     * server has the gifts.
     */
    async purchaseGifts(quantity: number): Promise<PaymentResult> {
        const isNative = platformService.isNative();
        try {
            const response = await coreApiClient.axios.post<{ checkoutUrl: string; sessionId?: string }>(
                '/api/hallouminati/gifts/checkout',
                { quantity, native: isNative || undefined },
            );
            if (isNative) {
                return this.completeNativeStripeCheckout(
                    response.data.checkoutUrl,
                    response.data.sessionId,
                    (status) => (status.gift_codes ?? 0) > 0,
                );
            }
            return { success: true, checkoutUrl: response.data.checkoutUrl };
        } catch (error) {
            Sentry.captureException(error, { tags: { error_type: 'gift_checkout', native: String(isNative) } });
            const data = (error as { response?: { data?: { error?: string } } })?.response?.data;
            return {
                success: false,
                error: data?.error || (error instanceof Error ? error.message : 'Failed to create payment session'),
            };
        }
    }

    /**
     * Whether the installed native binary honors dynamic product options.
     * OTA web updates run inside older binaries whose plugin lacks
     * getCapabilities() entirely — those reject, meaning "no".
     */
    async getNativeCapabilities(): Promise<PaywallCapabilities> {
        if (!platformService.isNative()) {
            return { dynamicProducts: false };
        }
        try {
            const caps = await PaywallPlugin.getCapabilities();
            return { dynamicProducts: caps?.dynamicProducts === true };
        } catch {
            return { dynamicProducts: false };
        }
    }

    /** Check a refer-a-friend code before offering the discounted purchase. */
    async validateReferralCode(code: string): Promise<ReferralCodeValidation> {
        const platform = platformService.isNative()
            ? (platformService.isIOS() ? 'apple' : 'google')
            : 'stripe';
        try {
            const response = await coreApiClient.axios.post<{ success: boolean; data: ReferralCodeValidation }>(
                '/api/payments/referral-code/validate',
                { code, platform },
            );
            return response.data.data ?? { valid: false };
        } catch (error) {
            Sentry.captureException(error, { tags: { error_type: 'referral_code_validate' } });
            return { valid: false, reason: 'request_failed' };
        }
    }

    async restorePurchases(): Promise<{ restored: boolean; error?: string }> {
        if (!platformService.isNative()) {
            return { restored: false, error: 'Restore is only available in the app' };
        }

        try {
            const result = await PaywallPlugin.restorePurchases();
            return result;
        } catch (error) {
            Sentry.captureException(error, { tags: { error_type: 'payment_restore' } });
            return {
                restored: false,
                error: error instanceof Error ? error.message : 'Restore failed',
            };
        }
    }

    private async purchaseViaNative(options?: PurchaseOptions): Promise<PaymentResult> {
        try {
            // Old binaries ignore the options and purchase their built-in
            // default product — the backend accepts the legacy product id.
            const result = await PaywallPlugin.showPaywall(options);
            return result;
        } catch (error) {
            Sentry.captureException(error, { tags: { error_type: 'payment_native' } });
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Purchase failed',
            };
        }
    }

    private async purchaseViaStripe(options?: PurchaseOptions): Promise<PaymentResult> {
        const isNative = platformService.isNative();
        try {
            const response = await coreApiClient.axios.post<{ checkoutUrl: string; sessionId?: string }>(
                '/api/payments/create-session',
                {
                    product: 'hallouminati_guide_lifetime',
                    price: 2000,
                    currency: 'eur',
                    app_type: 'hallouminati',
                    // Refer-a-friend code — backend re-validates and applies the coupon.
                    referral_code: options?.referralCode ?? undefined,
                    // Redirects hand back to the app's custom scheme instead of the site.
                    native: isNative || undefined,
                }
            );

            // Web/Telegram redirect the current page; native can't (the webview
            // would be handed off to the system browser and never come back), so
            // it drives the checkout itself.
            if (isNative) {
                return this.completeNativeStripeCheckout(response.data.checkoutUrl, response.data.sessionId);
            }

            return { success: true, checkoutUrl: response.data.checkoutUrl };
        } catch (error) {
            const data = (error as { response?: { data?: { error?: string; code?: string } } })?.response?.data;
            if (data?.code === 'already_pro') {
                // The server has this user as Pro and the app doesn't — an earlier
                // checkout landed without the app seeing it finish. That is a
                // completed purchase, not an error: unlock instead of a toast.
                Sentry.captureMessage('Paywall opened for a user the server already has as Pro', {
                    level: 'warning',
                    tags: { error_type: 'paywall_already_pro', native: String(isNative) },
                });
                return { success: true };
            }
            // Prefer the backend's message (e.g. "Invalid or expired discount code")
            // over axios' generic "Request failed with status code 422".
            const backendError = data?.error;
            return {
                success: false,
                error: backendError || (error instanceof Error ? error.message : 'Failed to create payment session'),
            };
        }
    }

    /**
     * Run a Stripe Checkout inside the native app and resolve once the purchase
     * is confirmed *by the server*.
     *
     * Checkout opens in an in-app browser (SFSafariViewController / Chrome
     * Custom Tab). Three independent signals can end it, because none is
     * reliable on its own:
     *
     *  1. the deep link from the return bridge page (fast path — but a custom
     *     scheme can be blocked),
     *  2. the browser being dismissed by the user (Android can't be closed
     *     programmatically, so this is often how a paid checkout ends), and
     *  3. polling checkout-status — the only authority. It asks Stripe directly
     *     and grants entitlement itself, so a webhook that is slow, dropped, or
     *     racing the redirect can't strand a buyer.
     *
     * Nothing here trusts the redirect: only the server saying is_pro (or, for
     * gifts, that the gifts exist) counts.
     *
     * While the custom tab is in front, Android cuts the app's network, so the
     * polls fail (or hang) and the first ones after returning can too. A
     * checkout is therefore never written off as cancelled until the server has
     * answered a poll sent after the user came back; a paid buyer whose sheet
     * closed on a dead connection used to land back on the paywall.
     */
    private async completeNativeStripeCheckout(
        checkoutUrl: string,
        sessionId?: string,
        // What "done" means: Adept checkouts end when the buyer is pro; gift
        // checkouts when their gifts exist (the buyer may be an Adept already).
        isFulfilled: (status: CheckoutStatus) => boolean = (status) => status.is_pro,
    ): Promise<PaymentResult> {
        if (!sessionId) {
            // Pre-sessionId backend: we'd have no way to confirm the purchase,
            // and sending the user out to the system browser would strand them.
            Sentry.captureMessage('Native Stripe checkout started without a sessionId', {
                level: 'error',
                tags: { error_type: 'payment_native_stripe' },
            });
            return { success: false, error: 'Payment could not be started' };
        }

        // Set by the return bridge / browser dismissal; starts the grace window.
        let returned: 'success' | 'cancelled' | null = null;
        let returnedAt: number | null = null;
        // A poll sent after the user came back got an answer from the server.
        let answeredSinceReturn = false;
        let checkNow = false;
        const handles: PluginListenerHandle[] = [];

        const markReturned = (outcome: 'success' | 'cancelled') => {
            returnedAt = returnedAt ?? Date.now();
            checkNow = true;
            return outcome;
        };

        try {
            handles.push(await Browser.addListener('browserFinished', () => {
                // The user closed the sheet. That's usually a cancel, but a paid
                // checkout on Android also ends this way — the grace poll decides.
                returned = returned ?? markReturned('cancelled');
            }));

            handles.push(await CapacitorApp.addListener('appUrlOpen', (event: { url: string }) => {
                if (!event.url.includes('payment/')) return;
                returned = markReturned(event.url.includes('payment/cancelled') ? 'cancelled' : 'success');
                // iOS: dismiss the sheet. No-op on Android, where returning to the
                // app has already put us in front of the custom tab.
                void Browser.close().catch(() => { });
            }));

            // Back in front (network restored): poll now rather than on the next beat.
            handles.push(await CapacitorApp.addListener('resume', () => {
                checkNow = true;
            }));

            await Browser.open({ url: checkoutUrl, presentationStyle: 'fullscreen' });

            const startedAt = Date.now();
            let lastPollAt = 0;
            let graceUntil: number | null = null;

            for (;;) {
                await delay(CHECKOUT_TICK_MS);

                const now = Date.now();
                const due = checkNow || now - lastPollAt >= CHECKOUT_POLL_MS;
                if (due) {
                    checkNow = false;
                    lastPollAt = now;

                    const status = await this.getCheckoutStatus(sessionId);
                    if (status && returnedAt !== null && now >= returnedAt) {
                        answeredSinceReturn = true;
                    }
                    if (status && isFulfilled(status)) {
                        void Browser.close().catch(() => { });
                        return { success: true };
                    }
                    if (status?.status === 'expired') {
                        return { success: false, cancelled: true };
                    }
                }

                // Once the user is back, keep checking briefly — payment may still
                // be settling — then take `returned` at its word.
                if (returned && graceUntil === null) {
                    graceUntil = Date.now() + CHECKOUT_GRACE_MS;
                }
                if (graceUntil !== null && Date.now() > graceUntil) {
                    if (!answeredSinceReturn && returnedAt !== null) {
                        // Nothing the server said since the user came back — so
                        // nothing to call this a cancel on. Keep asking a while.
                        if (Date.now() - returnedAt < CHECKOUT_UNREACHABLE_MS) continue;
                        Sentry.captureMessage('Stripe checkout ended with the server unreachable', {
                            level: 'error',
                            tags: { error_type: 'payment_native_stripe' },
                            extra: { sessionId, returned },
                        });
                        return {
                            success: false,
                            error: 'We couldn’t confirm your payment yet. If you paid, reopen the app in a minute.',
                        };
                    }
                    if (returned === 'success') {
                        // Stripe redirected to success but entitlement never landed.
                        Sentry.captureMessage('Stripe checkout returned success without entitlement', {
                            level: 'error',
                            tags: { error_type: 'payment_native_stripe' },
                            extra: { sessionId },
                        });
                        return {
                            success: false,
                            error: 'Payment received but access is still activating. Please reopen the app in a minute.',
                        };
                    }
                    return { success: false, cancelled: true };
                }

                if (Date.now() - startedAt > CHECKOUT_TIMEOUT_MS) {
                    Sentry.captureMessage('Stripe checkout timed out waiting for confirmation', {
                        level: 'warning',
                        tags: { error_type: 'payment_native_stripe' },
                        extra: { sessionId },
                    });
                    return { success: false, cancelled: true };
                }
            }
        } catch (error) {
            Sentry.captureException(error, { tags: { error_type: 'payment_native_stripe' } });
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Purchase failed',
            };
        } finally {
            await Promise.all(handles.map((handle) => handle.remove().catch(() => { })));
        }
    }

    /**
     * One checkout-status poll. Network blips must not read as "not paid", so
     * failures return null (keep waiting) rather than false.
     */
    private async getCheckoutStatus(sessionId: string): Promise<CheckoutStatus | null> {
        try {
            // Short timeout: a poll sent while Android had the app's network cut
            // can hang, and the loop waits on it before it can ask again.
            const response = await coreApiClient.axios.get<CheckoutStatus>(
                '/api/payments/checkout-status',
                { params: { session_id: sessionId }, timeout: CHECKOUT_POLL_TIMEOUT_MS },
            );
            return response.data;
        } catch (error) {
            const status = (error as { response?: { status?: number } })?.response?.status;
            // 403/404 mean this session will never confirm — stop pretending.
            if (status === 403 || status === 404) {
                Sentry.captureException(error, {
                    tags: { error_type: 'payment_native_stripe' },
                    extra: { sessionId, httpStatus: status },
                });
                return { paid: false, status: 'expired', is_pro: false };
            }
            return null;
        }
    }

    // --- GreekBrain monthly subscription (recurring; separate from the
    //     Hallouminati one-time guide purchase above) ---

    /**
     * Start a GreekBrain subscription.
     *  - Android: Google Play requires its own billing for digital goods, so go
     *    through the native plugin (which launches the subscription product and
     *    verifies the purchaseToken with the backend).
     *  - Web: Stripe Checkout URL to redirect to.
     *  - iOS: Stripe Checkout for now (native subscription IAP not wired yet).
     */
    async subscribeGreekBrain(): Promise<PaymentResult> {
        if (platformService.isAndroid()) {
            return this.purchaseViaNative();
        }
        // iOS: the native StoreKit sheet (GreekBrainPaywall.swift) — Apple
        // requires IAP for the subscription. Old GreekBrain binaries had no
        // PaywallPlugin at all; the rejection surfaces as a plain error.
        if (platformService.isIOS()) {
            return this.purchaseViaNative({ productId: GREEKBRAIN_IOS_PRODUCT_ID });
        }

        try {
            const response = await coreApiClient.axios.post<{ checkoutUrl: string }>(
                '/api/payments/greekbrain/subscribe'
            );
            return { success: true, checkoutUrl: response.data.checkoutUrl };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to start subscription',
            };
        }
    }

    /** Cancel at period end — access remains until the current period expires. */
    async cancelGreekBrain(): Promise<{ success: boolean; cancels_at?: string; error?: string }> {
        try {
            const response = await coreApiClient.axios.post<{ success: boolean; cancels_at?: string }>(
                '/api/payments/greekbrain/cancel'
            );
            return { success: true, cancels_at: response.data.cancels_at };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to cancel subscription',
            };
        }
    }

    /** Resume a subscription that was cancelled but is still within its paid period. */
    async resumeGreekBrain(): Promise<{ success: boolean; error?: string }> {
        try {
            await coreApiClient.axios.post('/api/payments/greekbrain/resume');
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to resume subscription',
            };
        }
    }
}

export const paymentService = new PaymentService();
